import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@material-ui/core';
import ModalDialog from 'src/common/refactoredComponents/modalDialog/modalDialog';
import Button from 'src/common/refactoredComponents/button/Button';
import Spinner from 'src/common/refactoredComponents/spinner/Spinner';
import { ButtonStyles } from 'src/constants/buttonStyles';
import TabControl from 'src/common/refactoredComponents/tabControl/tabControl';
import AdditionalDetailsTab from './AdditionalDetailsTab';
import SetContentTab from './SetContentTab';
import BottomNavigation from 'src/common/refactoredComponents/bottomNavigation/BottomNavigation';
import { sizes } from 'src/styles/Media.styled';
import { getBatchExportRequest } from 'src/common/redux/actions';
import { BASE_TEXT_PX, GREY_COLOR, TERTIARY_FONT_COLOR, TITLE_FONT_COLOR } from 'src/styles/fonts/MCFontConstants';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import { accountNumberZeroTrimmer } from 'src/utils/util';

import {
    BatchPopupTitle,
    BatchNumber,
    BatchPopupTitleLabel,
    BatchPopupTitleStatus,
    BatchPopupTitleStatusValue,
    BatchPopupHeader,
    BatchPopupScrollContainer,
    BatchPopupShell,
    BatchPopupContent,
    SummaryGrid,
    SummaryItem,
    SummaryLabel,
    SummaryValue,
    TabControlSection,
    SummaryDetails,
    LoadingSpinnerContainer
} from './BatchPopup.styled';

const SUMMARY_FIELDS = [
    { key: 'brand', label: 'setSummary.brand' },
    { key: 'deliveryStatus', label: 'setSummary.deliveryStatus' },
    { key: 'currentLocation', label: 'setSummary.currentLocation' },
];

// Maps raw API set status text to its translation key; unmapped statuses fall back to the raw value.
const SET_STATUS_TRANSLATION_KEYS = {
    'Allocated': 'setStatuses.allocated',
    'In Transit': 'setStatuses.inTransit',
    'Kit Full': 'setStatuses.kitFull',
    'Kit Partially Replenished': 'setStatuses.kitPartiallyReplenished',
    'At Location': 'setStatuses.atLocation',
    'Disputed': 'setStatuses.disputed',
};

const translateSetStatus = (t, status) => {
    const key = SET_STATUS_TRANSLATION_KEYS[status];
    return key ? t(key) : formatValue(status);
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }

    if (Array.isArray(value)) {
        return value.filter(Boolean).join(' - ') || '';
    }

    return String(value);
};

const formatAccountLocation = (location) => {
    if (!location || typeof location !== 'object') {
        return location;
    }

    const accountName = location.currentAccName || location.accountName || '';
    const accountNumber = accountNumberZeroTrimmer(location.currentAccNum || location.accountNumber || '');

    if (accountName && accountNumber) {
        return `${accountName} ${accountNumber}`;
    }

    return accountName || accountNumber || null;
};

const hasBatchDetailsData = (details) => {
    if (!details || typeof details !== 'object' || Array.isArray(details)) {
        return false;
    }

    return Object.values(details).some((value) => {
        if (value === null || value === undefined || value === '') {
            return false;
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        if (typeof value === 'object') {
            return Object.keys(value).length > 0;
        }

        return true;
    });
};

const BatchPopup = ({
    batchDetails = {},
    isOpen = true,
    isLoading = false,
    onClose,
    onExport,
    batchId,
    setNumber,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // Use useMediaQuery to reactively track viewport changes (orientation change, window resize)
    const isMobileViewport = useMediaQuery(`(max-width:${sizes.mobileDevice}px)`);
    const [shouldRenderTabs, setShouldRenderTabs] = useState(!isMobileViewport);
    const isBatchDataReady = hasBatchDetailsData(batchDetails);
    const batchExportState = useSelector(
        state => state.batchExportReducer || state.batchExport || {}
    );

    const allowPopupScroll = (event) => {
        event.stopPropagation();
    };

    useEffectWithErrorLogging(() => {
        if (!isOpen) {
            return;
        }

        if (isLoading || !isBatchDataReady) {
            setShouldRenderTabs(false);
            return;
        }

        if (!isMobileViewport || typeof window === 'undefined') {
            setShouldRenderTabs(true);
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            setShouldRenderTabs(true);
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [isLoading, isBatchDataReady, isMobileViewport, isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const setDetails = batchDetails?.set || {};
    const normalizedSummary = {
        brand: setDetails.brand,
        deliveryStatus: setDetails.deliveryStatus,
        currentLocation: formatAccountLocation(setDetails.currentLocation),
    };

    const handleExport = () => {
        // Use the props passed in, or fall back to extracting from batchDetails
        let finalBatchId = batchId;
        let finalSetNumber = setNumber;

        if (!finalBatchId || !finalSetNumber) {
            console.error('Batch ID or Set Number is missing.');
            return;
        }

        // Dispatch the export action to Redux
        dispatch(getBatchExportRequest({ batchID: finalBatchId, setNumber: finalSetNumber }));

        // Call the onExport callback if provided
        if (onExport && typeof onExport === 'function') {
            onExport();
        }
    };

    const popupTitle = (
        <BatchPopupTitle>
                <BatchNumber>{`${setNumber}.${batchId}`}</BatchNumber>
                <BatchPopupTitleStatus>
                    <BatchPopupTitleLabel>{t('setSummary.setStatus')}:</BatchPopupTitleLabel>
                    <BatchPopupTitleStatusValue>
                        {translateSetStatus(t, setDetails.setStatus || batchDetails.setStatus)}
                    </BatchPopupTitleStatusValue>
                </BatchPopupTitleStatus>
        </BatchPopupTitle>
    );

    return (
        <ModalDialog
            modalHeight={'calc(100vh - 110px)'}
            modalWidth={'70%'}
            smalModalWidth={'85%'}
            marginTop={'7.5rem'}
            mbPaddingBottom={'0px'}
            left={'7%'}
            right={'7%'}
            top={'7%'}
            bottom={'10%'}
            padding={'0px'}
            handleClose={onClose}
            isIPadPotrait={false}
            zIndex={'2000'}
            id={'batch-popup-modal'}
            backdropColor={isMobileViewport ? 'transparent' : undefined}
        >
            <BatchPopupShell>
                <BatchPopupScrollContainer
                    onTouchMoveCapture={allowPopupScroll}
                    onWheelCapture={allowPopupScroll}
                >
                    <BatchPopupContent>
                        <SummaryDetails>
                        <BatchPopupHeader>
                            {popupTitle}
                        </BatchPopupHeader>

                        <SummaryGrid>
                            {SUMMARY_FIELDS.map(({ key, label }) => (
                                <SummaryItem key={key}>
                                    <SummaryLabel>{t(label)}: </SummaryLabel>
                                    <SummaryValue>{formatValue(normalizedSummary[key])}</SummaryValue>
                                </SummaryItem>
                            ))}
                        </SummaryGrid>
                        </SummaryDetails>

                        {isLoading || !isBatchDataReady || !shouldRenderTabs ? (
                            <TabControlSection>
                                <LoadingSpinnerContainer>
                                    <Spinner
                                        id="batchPopup-loadingSpinner"
                                        animation="border"
                                        variant="primary"
                                        small
                                    />
                                </LoadingSpinnerContainer>
                            </TabControlSection>
                        ) : (
                            <TabControlSection>
                                <TabControl
                                    id="batch-popup-tab-control"
                                    useLabel={true}
                                    tabOneTitle={<span id="batch-popup-tab-additional-details">{t('additionalDetailsTabs.tabTitle')}</span>}
                                    tabOneId="batch-popup-tab-additional-details"
                                    tabOneChildren={
                                        <AdditionalDetailsTab content={batchDetails.additionalDetails} />
                                    }
                                    tabTwoTitle={<span id="batch-popup-tab-set-content">{t('setContent.tabTitle')}</span>}
                                    tabTwoId="batch-popup-tab-set-content"
                                    tabTwoChildren={<SetContentTab content={batchDetails.setContent} />}
                                    tabViewWidth="fit-content"
                                    tabViewWidthMediumDevice="fit-content"
                                    tabViewWidthLargeDevice="fit-content"
                                    widthForMobileScreen="fit-content"
                                    tabViewHeight="48px"
                                    tabViewMargin="0"
                                    tabWidth="auto"
                                    dkPadding="8px 14px"
                                    tabFontColor={TITLE_FONT_COLOR}
                                    inactiveTabFontColor={TERTIARY_FONT_COLOR}
                                    border={`1px solid ${GREY_COLOR}`}
                                    borderRadius="6px 6px 0 0"
                                    mobileTabColumns={2}
                                />
                            </TabControlSection>
                        )}
                    </BatchPopupContent>
                </BatchPopupScrollContainer>

                <BottomNavigation
                    className={'Bottom-footer'}
                    justifyContent={'flex-end'}
                    alignItems={'center'}
                    opacity={'1'}
                    position={'sticky'}
                    mbPosition={'fixed'}
                    buttonsPosition={'sticky'}
                    width={{
                        standard: '100%',
                        portraitDevice: '100%',
                        smallDevice: '100%',
                    }}
                    padding={isMobileViewport ? '11px' : '16px 30px'}
                    mbPadding={'11px'}
                    mobileJustifyContent={'center'}
                    smallDeviceJustifyContent={'center'}
                    isIPadPotrait={true}
                    iPadPortraitJustifyContent={'center'}
                    bottom={'0'}
                >
                    <Button
                        id="batch-popup-export-button"
                        clicked={handleExport}
                        variant={ButtonStyles?.secondary}
                        width="120px"
                        mobileWidth="120px"
                        ipadWidth="120px"
                        height="38px"
                        fontSize={BASE_TEXT_PX}
                        borderRadius="25px"
                        marginRight="12px"
                        mobileMargin="0 8px 0 0"
                        disabled={batchExportState?.isLoading || isLoading}
                    >
                        {batchExportState?.isLoading ? (
                            <Spinner
                                id="more-fieldInventoryDashboard-export-loadingSpinner"
                                animation="border"
                                variant="primary"
                                small={true}
                            />
                        ) : 'Export'}
                    </Button>
                    <Button
                        id="batch-popup-close-button"
                        clicked={onClose}
                        variant={ButtonStyles?.primary}
                        width="120px"
                        mobileWidth="120px"
                        ipadWidth="120px"
                        height="38px"
                        fontSize={BASE_TEXT_PX}
                        borderRadius="25px"
                    >
                        Close
                    </Button>
                </BottomNavigation>
            </BatchPopupShell>
        </ModalDialog>
    );
};

BatchPopup.propTypes = {
    batchDetails: PropTypes.object,
    isOpen: PropTypes.bool,
    isLoading: PropTypes.bool,
    onClose: PropTypes.func,
    onExport: PropTypes.func,
    batchId: PropTypes.string,
    setNumber: PropTypes.string,
};

BatchPopup.defaultProps = {
    batchDetails: {},
    isOpen: true,
    isLoading: false,
    onClose: () => {},
    onExport: () => {},
    batchId: null,
    setNumber: null,
};

export default BatchPopup;